// src/features/calendar/RecurrenceEditor.tsx
// Recurrence rule form section: frequency, interval, weekdays, end condition.

import { useI18n } from '@react/hooks';
import { Input } from '@react/components';
import { Select } from '@/components/Select';
import type { RecurrenceRule, RecurrenceWeekday } from '@core/types/calendar';
import { WEEKDAYS, WEEKDAY_KEYS } from './time';

interface RecurrenceEditorProps {
  value: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
}

type EndMode = 'never' | 'count' | 'until';

function endMode(rule: RecurrenceRule): EndMode {
  if (rule.count !== undefined && rule.count !== null) return 'count';
  if (rule.until) return 'until';
  return 'never';
}

const EMPTY_RULE: RecurrenceRule = {
  frequency: 'RECURRENCE_FREQUENCY_WEEKLY',
  interval: 1,
  by_day: [],
  by_month_day: [],
  by_month: [],
  by_set_pos: [],
  by_year_day: [],
  by_week_no: [],
};

export function RecurrenceEditor({ value, onChange }: RecurrenceEditorProps) {
  const { t } = useI18n();

  const freqOptions = [
    { value: 'none', label: t('calendar.recurrence.none') },
    { value: 'RECURRENCE_FREQUENCY_DAILY', label: t('calendar.recurrence.daily') },
    { value: 'RECURRENCE_FREQUENCY_WEEKLY', label: t('calendar.recurrence.weekly') },
    { value: 'RECURRENCE_FREQUENCY_MONTHLY', label: t('calendar.recurrence.monthly') },
    { value: 'RECURRENCE_FREQUENCY_YEARLY', label: t('calendar.recurrence.yearly') },
  ];

  const rule = value;

  const setFrequency = (freq: string) => {
    if (freq === 'none') {
      onChange(null);
      return;
    }
    onChange({ ...(rule ?? EMPTY_RULE), frequency: freq as RecurrenceRule['frequency'] });
  };

  const toggleDay = (day: RecurrenceWeekday) => {
    if (!rule) return;
    const has = rule.by_day.some((r) => r.day === day);
    onChange({
      ...rule,
      by_day: has ? rule.by_day.filter((r) => r.day !== day) : [...rule.by_day, { day }],
    });
  };

  const setEnd = (mode: EndMode) => {
    if (!rule) return;
    const next = { ...rule };
    delete next.count;
    delete next.until;
    if (mode === 'count') next.count = 10;
    if (mode === 'until') next.until = new Date().toISOString();
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <Select
        label={t('calendar.recurrence.repeats')}
        value={rule?.frequency ?? 'none'}
        onChange={(e) => setFrequency(e.target.value)}
        options={freqOptions}
      />
      {rule && (
        <>
          <div className="flex items-end gap-2">
            <Input
              label={t('calendar.recurrence.interval')}
              type="text"
              inputMode="numeric"
              value={rule.interval > 0 ? String(rule.interval) : ''}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                onChange({ ...rule, interval: v === '' ? 0 : parseInt(v, 10) });
              }}
              helperText={t('calendar.recurrence.intervalHint')}
            />
          </div>
          {rule.frequency === 'RECURRENCE_FREQUENCY_WEEKLY' && (
            <div>
              <span className="mb-1 block text-sm font-medium text-foreground">
                {t('calendar.recurrence.onDays')}
              </span>
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((day) => {
                  const active = rule.by_day.some((r) => r.day === day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleDay(day)}
                      className={`rounded-md border px-2.5 py-1 text-xs ${
                        active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background text-foreground hover:bg-muted'
                      }`}
                    >
                      {t(`calendar.weekday.${WEEKDAY_KEYS[day]}`)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Select
              label={t('calendar.recurrence.ends')}
              value={endMode(rule)}
              onChange={(e) => setEnd(e.target.value as EndMode)}
              options={[
                { value: 'never', label: t('calendar.recurrence.endsNever') },
                { value: 'count', label: t('calendar.recurrence.endsAfter') },
                { value: 'until', label: t('calendar.recurrence.endsOn') },
              ]}
            />
            {endMode(rule) === 'count' && (
              <Input
                label={t('calendar.recurrence.occurrences')}
                type="text"
                inputMode="numeric"
                value={rule.count !== undefined && rule.count > 0 ? String(rule.count) : ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '');
                  onChange({ ...rule, count: v === '' ? 0 : parseInt(v, 10) });
                }}
              />
            )}
            {endMode(rule) === 'until' && (
              <Input
                label={t('calendar.recurrence.untilDate')}
                type="date"
                value={rule.until ? rule.until.slice(0, 10) : ''}
                onChange={(e) => onChange({ ...rule, until: e.target.value ? new Date(e.target.value).toISOString() : rule.until })}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
